class FloatingNav extends HTMLElement {
  connectedCallback() {
    this.cartItemCount = 0;
    this.cartFirstProductId = null;
    this.lastRecommendationSeedProductId = null;
    this.postAddTimeout = null;
    this.drawerCloseDelay = 1000;
    this.drawerCloseTimeout = null;
    this._eventListeners = [];
    this._carouselStates = new WeakMap();
    this._heightAnimationStates = new Map();
    this.isHoverDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this._typingIndicator = null;

    this._bindElements();
    this._initCustomerServiceConfig();
    if (!this._shouldRenderForViewport()) {
      this.hidden = true;
      return;
    }
    this._bindEvents();
    this._initChatbot();
    this._switchMainTab('home', { animateHeight: false });
    this._switchHelpTab('faq', { animateHeight: false });
    this._initCarousels();
    this._fetchCart();
  }

  disconnectedCallback() {
    if (this._viewportMediaMobile && this._handleViewportChange) {
      if (this._viewportMediaMobile.removeEventListener) {
        this._viewportMediaMobile.removeEventListener('change', this._handleViewportChange);
        this._viewportMediaDesktop.removeEventListener('change', this._handleViewportChange);
      } else {
        this._viewportMediaMobile.removeListener(this._handleViewportChange);
        this._viewportMediaDesktop.removeListener(this._handleViewportChange);
      }
    }
    this._eventListeners.forEach(({ target, eventName, handler, options }) => {
      target.removeEventListener(eventName, handler, options);
    });
    this._eventListeners = [];

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }

    if (this.postAddTimeout) {
      window.clearTimeout(this.postAddTimeout);
      this.postAddTimeout = null;
    }
    this._clearCloseDrawerTimeout();
    this._clearHeightAnimation(this.tabPanelsContainer);
    this._clearHeightAnimation(this.helpContent);

    this.querySelectorAll('[data-floating-carousel]').forEach((carousel) => {
      this._destroyCarousel(carousel);
    });
  }

  _bindElements() {
    this.launcher = this.querySelector('.floating-nav__launcher');
    this.drawer = this.querySelector('.floating-nav__drawer');
    this.tabButtons = this.querySelectorAll('.floating-nav__tab-button');
    this.tabPanelsContainer = this.querySelector('.floating-nav__tab-panels');
    this.tabPanels = this.querySelectorAll('.floating-nav__tab-panel');

    this.homeEmpty = this.querySelector('[data-home-empty]');
    this.homeCart = this.querySelector('[data-home-cart]');
    this.recommendationCarousel = this.querySelector('[data-carousel-type="recommendations"]');
    this.recommendationTrack = this.querySelector('[data-floating-recommendation-track]');
    this.recommendationEmpty = this.querySelector('[data-floating-recommendation-empty]');
    // Initialize carousel as hidden until cart has items
    if (this.recommendationCarousel) this.recommendationCarousel.hidden = true;
    this.overrideSlideTemplate = this.querySelector('template[data-floating-override-slide]');

    this.cartEmpty = this.querySelector('[data-floating-cart-empty]');
    this.cartContent = this.querySelector('[data-floating-cart-content]');
    this.cartCount = this.querySelector('.floating-nav__cart-count');
    this.lineItems = this.querySelector('.floating-nav__line-items');
    this.subtotal = this.querySelector('[data-floating-subtotal]');
    this.postAddBanner = this.querySelector('[data-post-add-banner]');

    this.helpClose = this.querySelector('.floating-nav__help-close');
    this.helpTabs = this.querySelectorAll('.floating-nav__help-tab');
    this.helpContent = this.querySelector('.floating-nav__help-content');
    this.helpPanels = this.querySelectorAll('.floating-nav__help-panel');
    this.chatbot = this.querySelector('[data-chatbot]');
    this.chatThread = this.querySelector('[data-chat-thread]');
    this.chatActions = this.querySelector('[data-chat-actions]');
    this.chatRootOptionsTemplate = this.querySelector('template[data-chat-root-options-template]');
    this.contactForm = this.querySelector('.floating-nav__contact-form');

    if (this.drawer) {
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.inert = true;
    }
  }

  _shouldRenderForViewport() {
    const showMobile = this.dataset.showMobile === 'true';
    const showDesktop = this.dataset.showDesktop === 'true';
    const isMobile = window.matchMedia('(max-width: 989.98px)').matches;

    return isMobile ? showMobile : showDesktop;
  }

  _updateViewportVisibility() {
    const shouldShow = this._shouldRenderForViewport();
    this.hidden = !shouldShow;

    if (!shouldShow) {
      this.classList.remove('is-visible');
      this._closeDrawer();
      return;
    }

    this.classList.toggle('is-visible', window.scrollY > 100);
  }

  _bindEvents() {
    this._viewportMediaMobile = window.matchMedia('(max-width: 989.98px)');
    this._viewportMediaDesktop = window.matchMedia('(min-width: 990px)');
    this._handleViewportChange = () => this._updateViewportVisibility();
    if (this._viewportMediaMobile.addEventListener) {
      this._viewportMediaMobile.addEventListener('change', this._handleViewportChange);
      this._viewportMediaDesktop.addEventListener('change', this._handleViewportChange);
    } else {
      this._viewportMediaMobile.addListener(this._handleViewportChange);
      this._viewportMediaDesktop.addListener(this._handleViewportChange);
    }
    const onScroll = () => {
      if (this.hidden) return;
      this.classList.toggle('is-visible', window.scrollY > 100);
    };
    this._addEvent(window, 'scroll', onScroll, { passive: true });
    onScroll();

    if (this.launcher) {
      this._addEvent(this.launcher, 'click', (event) => {
        event.preventDefault();
        if (this.classList.contains('is-open')) {
          this._closeDrawer();
        } else {
          this._openDrawer('home');
        }
      });
    }

    if (this.isHoverDevice) {
      const keepDrawerOpen = () => {
        this._clearCloseDrawerTimeout();
      };
      const openDrawerFromLauncherHover = () => {
        this._clearCloseDrawerTimeout();
        if (!this.classList.contains('is-open')) {
          this._openDrawer(this.activeMainTab || 'home');
        }
      };
      if (this.launcher) {
        this._addEvent(this.launcher, 'mouseenter', openDrawerFromLauncherHover);
      }

      this._addEvent(this, 'mouseenter', keepDrawerOpen);
      this._addEvent(this, 'mouseleave', () => this._scheduleCloseDrawer());
      this._addEvent(this, 'focusin', keepDrawerOpen);
      this._addEvent(this, 'focusout', () => {
        window.requestAnimationFrame(() => {
          if (this.contains(document.activeElement)) return;
          if (this.matches(':hover')) return;
          this._scheduleCloseDrawer();
        });
      });
    }

    this.tabButtons.forEach((button) => {
      this._addEvent(button, 'click', () => {
        const tabId = button.dataset.tab;
        this._openDrawer(tabId);
      });
    });

    if (this.helpClose) {
      this._addEvent(this.helpClose, 'click', () => this._closeDrawer());
    }

    this.helpTabs.forEach((tab) => {
      this._addEvent(tab, 'click', () => this._switchHelpTab(tab.dataset.tab));
    });

    if (this.chatActions) {
      this._addEvent(this.chatActions, 'click', (event) => this._handleChatActionClick(event));
      this._addEvent(this.chatActions, 'submit', (event) => this._handleChatActionSubmit(event));
    }

    if (this.contactForm) {
      this._addEvent(this.contactForm, 'submit', (event) => this._handleContactSubmit(event));
    }

    this._addEvent(document, 'click', (event) => {
      const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const clickedInsideFloatingNav = eventPath.includes(this) || this.contains(event.target);
      if (this.classList.contains('is-open') && !clickedInsideFloatingNav) {
        this._closeDrawer();
      }
    });

    this._addEvent(document, 'keydown', (event) => {
      if (event.key === 'Escape') this._closeDrawer();
    });

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        this._onCartUpdate(payload);
      });
    }
  }

  _addEvent(target, eventName, handler, options) {
    if (!target) return;
    target.addEventListener(eventName, handler, options);
    this._eventListeners.push({ target, eventName, handler, options });
  }
  _scheduleCloseDrawer(delay = this.drawerCloseDelay) {
    this._clearCloseDrawerTimeout();
    this.drawerCloseTimeout = window.setTimeout(() => {
      this.drawerCloseTimeout = null;
      this._closeDrawer();
    }, delay);
  }

  _clearCloseDrawerTimeout() {
    if (!this.drawerCloseTimeout) return;
    window.clearTimeout(this.drawerCloseTimeout);
    this.drawerCloseTimeout = null;
  }

  _clearHeightAnimation(container) {
    if (!container) return;
    const state = this._heightAnimationStates.get(container);
    if (state?.onTransitionEnd) {
      container.removeEventListener('transitionend', state.onTransitionEnd);
    }
    if (state?.resetTimeout) {
      window.clearTimeout(state.resetTimeout);
    }
    this._heightAnimationStates.delete(container);
    container.style.height = '';
  }

  _animateContainerHeight(container, startHeight, endHeight) {
    if (!container) return;

    const from = Math.round(Number(startHeight) || 0);
    const to = Math.round(Number(endHeight) || 0);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || from <= 0 || to <= 0 || Math.abs(from - to) < 1) {
      this._clearHeightAnimation(container);
      return;
    }

    this._clearHeightAnimation(container);
    container.style.height = `${from}px`;
    void container.offsetHeight;

    window.requestAnimationFrame(() => {
      container.style.height = `${to}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.target !== container || event.propertyName !== 'height') return;
      this._clearHeightAnimation(container);
    };

    const resetTimeout = window.setTimeout(() => {
      this._clearHeightAnimation(container);
    }, 380);

    this._heightAnimationStates.set(container, { onTransitionEnd, resetTimeout });
    container.addEventListener('transitionend', onTransitionEnd);
  }

  _openDrawer(tabId = 'home') {
    this._clearCloseDrawerTimeout();
    this.classList.add('is-open');
    if (this.launcher) this.launcher.setAttribute('aria-expanded', 'true');
    if (this.drawer) {
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.inert = false;
    }
    if (tabId) this._switchMainTab(tabId);
  }

  _closeDrawer() {
    this._clearCloseDrawerTimeout();
    this.classList.remove('is-open');
    if (this.launcher) this.launcher.setAttribute('aria-expanded', 'false');
    if (this.drawer) {
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.inert = true;
    }
  }

  _switchMainTab(tabId, { animateHeight = true } = {}) {
    if (!tabId) return;
    this.activeMainTab = tabId;
    const previouslyActivePanel = this.querySelector('.floating-nav__tab-panel.is-active');
    const previousHeight = previouslyActivePanel?.offsetHeight || this.tabPanelsContainer?.offsetHeight || 0;

    this.tabButtons.forEach((button) => {
      const isActive = button.dataset.tab === tabId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    let activePanel = null;

    this.tabPanels.forEach((panel) => {
      const isActive = panel.dataset.panel === tabId;
      panel.classList.toggle('is-active', isActive);
      if (isActive) activePanel = panel;
    });

    if (!this.tabPanelsContainer || !activePanel) return;

    const targetHeight = activePanel.offsetHeight;
    if (animateHeight) {
      this._animateContainerHeight(this.tabPanelsContainer, previousHeight, targetHeight);
      return;
    }

    this._clearHeightAnimation(this.tabPanelsContainer);
  }

  _switchHelpTab(tabId, { animateHeight = true } = {}) {
    if (!tabId) return;

    const previouslyActivePanel = this.querySelector('.floating-nav__help-panel.is-active');
    const previousHeight = previouslyActivePanel?.offsetHeight || this.helpContent?.offsetHeight || 0;
    this.helpTabs.forEach((tab) => {
      const isActive = tab.dataset.tab === tabId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    let activePanel = null;

    this.helpPanels.forEach((panel) => {
      const isActive = panel.dataset.panel === tabId;
      panel.classList.toggle('is-active', isActive);
      if (isActive) activePanel = panel;
    });

    if (!this.helpContent || !activePanel) return;

    const targetHeight = activePanel.offsetHeight;
    if (animateHeight) {
      this._animateContainerHeight(this.helpContent, previousHeight, targetHeight);
      return;
    }

    this._clearHeightAnimation(this.helpContent);
  }

  // Every customer-service surface in this widget — the contact form, the
  // chatbot's "annet spørsmål" branch and the defect fallback — posts to the
  // same Supabase project. Resolve that once here rather than re-deriving a
  // base URL from the defect endpoint at three separate call sites.
  _initCustomerServiceConfig() {
    let base = (this.dataset.supabaseUrl || window.__supabaseUrl || '').trim();

    if (!base) {
      // Fall back to whichever function endpoint the merchant did configure.
      const configured = [this.dataset.contactEndpoint, this.dataset.defectTicketEndpoint, this.dataset.orderStatusEndpoint]
        .map((value) => String(value || '').trim())
        .find(Boolean);
      const match = configured && configured.match(/^(https:\/\/[^/]+)/);
      if (match) base = match[1];
    }

    this.csConfig = {
      supabaseUrl: base.replace(/\/+$/, ''),
      anonKey: (this.dataset.supabaseAnonKey || window.__supabaseAnonKey || window.floatingNavAnonKey || '').trim(),
      supportEmail: this.dataset.supportEmail || 'post@kulkid.no',
      contactEndpoint: (this.dataset.contactEndpoint || '').trim(),
      defectTicketEndpoint: (this.dataset.defectTicketEndpoint || '').trim(),
      orderStatusEndpoint: (this.dataset.orderStatusEndpoint || '').trim(),
    };
  }

  // Returns '' when the widget has no way to reach the function, so callers
  // can fail loudly instead of posting into a guessed project.
  _functionUrl(name, configuredEndpoint) {
    const explicit = String(configuredEndpoint || '').trim();
    if (explicit) return explicit;
    if (!this.csConfig?.supabaseUrl) return '';
    return `${this.csConfig.supabaseUrl}/functions/v1/${name}`;
  }

  // Single entry point to the ticket pipeline. Everything the customer sends
  // us lands in `tickets`, which is what the buildings dashboard reads.
  async _submitCustomerServiceTicket({ name, email, body, subject, orderNumber, sourceChannel }) {
    const url = this._functionUrl('cs-contact-submit', this.csConfig?.contactEndpoint);
    if (!url) {
      throw new Error('Kundeservice-endepunktet er ikke konfigurert.');
    }

    const headers = { 'Content-Type': 'application/json' };
    if (this.csConfig?.anonKey) {
      headers.Authorization = `Bearer ${this.csConfig.anonKey}`;
      headers.apikey = this.csConfig.anonKey;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: name || '',
        email,
        body,
        subject: subject || '',
        orderNumber: orderNumber || '',
        source: 'contact_form',
        sourceChannel,
      }),
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (e) {
      payload = null;
    }

    if (!response.ok || payload?.success === false) {
      throw new Error(payload?.error || `HTTP ${response.status}`);
    }

    return payload || { success: true };
  }

  _initChatbot() {
    if (!this.chatbot || !this.chatThread || !this.chatActions) return;

    this.chatConfig = {
      supportEmail: this.dataset.supportEmail || 'post@kulkid.no',
      orderStatusEndpoint: this.dataset.orderStatusEndpoint || '',
      defectTicketEndpoint: this.dataset.defectTicketEndpoint || '',
      supabaseAnonKey: this.dataset.supabaseAnonKey || '',
      trackingUrlTemplate: this.dataset.trackingUrlTemplate || '',
    };

    // Parse delay feed if provided
    this.delayFeedEntries = [];
    if (this.dataset.orderDelayFeed) {
      try {
        this.delayFeedEntries = JSON.parse(this.dataset.orderDelayFeed);
      } catch (e) {
        console.warn('Failed to parse order delay feed:', e);
      }
    }

    this.chatSession = { flow: 'root', step: 'menu', data: {} };
    this._resetChatbotToRoot();
  }

  _resetChatbotToRoot() {
    if (!this.chatThread || !this.chatActions) return;

    this.chatThread.innerHTML = '';
    this._appendChatMessage('Hei og velkommen til KUL KID! 😊\nJeg er her for å hjelpe deg. Hva kan jeg hjelpe deg med?', { sender: 'bot', isDelayed: false });
    this._renderRootChatActions();
  }

  _renderRootChatActions() {
    if (!this.chatActions || !this.chatRootOptionsTemplate) return;
    
    const optionsClone = this.chatRootOptionsTemplate.content.cloneNode(true);
    this.chatActions.innerHTML = '';
    this.chatActions.append(optionsClone);
  }

  _handleChatActionClick(event) {
    const button = event.target.closest('[data-chat-action]');
    if (!button) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const action = button.dataset.chatAction;
    const isSilent = button.dataset.chatSilent === 'true';
    
    if (!isSilent) {
      this._appendChatMessage(button.textContent, { sender: 'user', isDelayed: false });
    }
    
    this._runChatAction(action);
  }

  async _handleChatActionSubmit(event) {
    console.log('[FloatingNav] _handleChatActionSubmit fired', { eventType: event.type, target: event.target.tagName });
    event.preventDefault();
    event.stopPropagation();
    
    const form = event.target.closest('form[data-chat-form-action]');
    console.log('[FloatingNav] Found form:', form ? 'yes' : 'no', form?.dataset?.chatFormAction);
    if (!form) return;
    
    const action = form.dataset.chatFormAction;
    console.log('[FloatingNav] Form action:', action);
    
    if (action === 'order_status_order_number') {
      await this._handleOrderNumberSubmit(form);
    } else if (action === 'defect_order_number') {
      console.log('[FloatingNav] Calling _handleDefectOrderSubmit');
      this._handleDefectOrderSubmit(form);
    } else if (action === 'defect_description') {
      this._handleDefectDescriptionSubmit(form);
    } else if (action === 'defect_email') {
      this._handleDefectEmailSubmit(form);
    } else if (action === 'defect_images') {
      await this._handleDefectImagesSubmit(form);
    }
  }

  _runChatAction(action) {
    switch (action) {
      case 'order_tracking':
        this._startOrderStatusFlow();
        break;
      case 'defect':
        this._startDefectFlow();
        break;
      case 'chat_restart':
        this._resetChatbotToRoot();
        break;
      case 'toll':
        this._renderStaticInfoResponse('toll');
        break;
      case 'retur':
        this._renderStaticInfoResponse('retur');
        break;
      case 'shipping_status':
        this._appendChatMessage('Spor ordren din eller kontakt oss direkte på post@kulkid.no.');
        this._renderChatActionStack({
          buttons: [
            { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
          ],
        });
        break;
      case 'kontakt':
        this._switchHelpTab('kontakt');
        break;
      case 'delay_wait':
        this._handleDelayWaitAction();
        break;
      case 'delay_change':
        this._handleDelayChangeAction();
        break;
      case 'defect_retry_upload':
        this._appendChatMessage('Last opp 2–3 bilder av feilen (maks 25 MB per bilde).');
        this._renderChatImageForm();
        break;
      case 'defect_type_missing':
        this.chatSession.data.issueType = 'manglende_del';
        this._appendChatMessage('Manglende del', { sender: 'user', isDelayed: false });
        this._appendChatMessage('Skjønner. Kan du beskrive hva som mangler?');
        this.chatSession.step = 'await_description';
        this._renderChatForm({
          submitAction: 'defect_description',
          label: 'Beskrivelse',
          placeholder: 'Hva mangler fra ordren...',
          buttonLabel: 'Fortsett',
          multiline: true,
        });
        break;
      case 'defect_type_defect':
        this.chatSession.data.issueType = 'defekt_del';
        this._appendChatMessage('Defekt del', { sender: 'user', isDelayed: false });
        this._appendChatMessage('Skjønner. Kan du beskrive feilen?');
        this.chatSession.step = 'await_description';
        this._renderChatForm({
          submitAction: 'defect_description',
          label: 'Beskrivelse',
          placeholder: 'Hva er galt med produktet...',
          buttonLabel: 'Fortsett',
          multiline: true,
        });
        break;
    }
  }

  _startOrderStatusFlow() {
    this.chatSession = { flow: 'order_status', step: 'await_order_number', data: {} };
    this._appendChatMessage('Skriv inn ordrenummeret ditt, så sjekker jeg status med en gang.');
    this._renderChatForm({
      submitAction: 'order_status_order_number',
      label: 'Ordrenummer',
      placeholder: '#1234',
      buttonLabel: 'Sjekk status',
    });
  }

  _startDefectFlow() {
    this.chatSession = { flow: 'defect', step: 'await_order_number', data: {} };
    this._appendChatMessage(
      'Selvfølgelig — jeg hjelper deg med reklamasjon. Start med ordrenummeret ditt.'
    );
    this._renderChatForm({
      submitAction: 'defect_order_number',
      label: 'Ordrenummer',
      placeholder: '1234',
      prefixLabel: '#',
      buttonLabel: 'Fortsett',
    });
  }

  async _handleOrderNumberSubmit(form) {
    const orderNumber = this._getChatFormValue(form);
    if (!orderNumber) return;

    this.chatSession = { flow: 'order_status', step: 'loading', data: { orderNumber } };
    this._appendChatMessage(orderNumber, { sender: 'user' });
    this._appendChatMessage('Sjekker ordren nå ...');
    this.chatActions.innerHTML = '';

    try {
      const orderStatus = await this._lookupOrderStatus(orderNumber);
      this.chatSession = { ...this.chatSession, step: 'resolved', data: { ...this.chatSession.data, orderStatus } };

      if (!orderStatus.found) {
        this._appendChatMessage(
          `Jeg fant dessverre ikke en ordre med nummer ${orderNumber}. Dobbeltsjekk nummeret og prøv igjen.`
        );
        this._renderChatActionStack({
          buttons: [
            { label: 'Prøv igjen', action: 'order_tracking', silent: true },
            { label: 'Kontakt oss', action: 'kontakt', silent: true },
            { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
          ],
        });
        return;
      }

      const statusText =
        orderStatus.statusText ||
        (orderStatus.hasTracking
          ? `Ordre ${orderStatus.orderNumber} er sendt.`
          : `Ordre ${orderStatus.orderNumber} er registrert og behandles.`);

      this._appendChatMessage(statusText);

      if (orderStatus.trackingNumber && !orderStatus.trackingUrl) {
        this._appendChatMessage(`Sporingsnummer: ${orderStatus.trackingNumber}`);
      }

      const links = [];
      if (orderStatus.trackingUrl) {
        links.push({ label: '📍 Åpne sporingslenke', href: orderStatus.trackingUrl });
      }
      if (orderStatus.manageOrderUrl) {
        links.push({ label: '✏️ Endre ordren', href: orderStatus.manageOrderUrl });
      }

      if (orderStatus.hasTracking) {
        this._renderChatActionStack({
          links,
          buttons: [
            { label: 'Sjekk en annen ordre', action: 'order_tracking', silent: true },
            { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
          ],
        });
        return;
      }

      const delayedItems = this._collectDelayedItems(orderStatus);
      if (delayedItems.length > 0) {
        const delaySummary = delayedItems
          .map((item) =>
            `• ${item.product}: ${item.eta || 'kommer snart'}${item.message ? ` (${item.message})` : ''}`
          )
          .join('\n');

        this.chatSession.data.delayedItems = delayedItems;

        this._appendChatMessage(
          `Ordren mangler sporingsnummer akkurat nå. Jeg ser følgende forsinkelse:\n${delaySummary}\n\nVil du vente på restock, eller ønsker du å endre ordren?`
        );

        this._renderChatActionStack({
          links,
          buttons: [
            { label: 'Jeg vil vente', action: 'delay_wait' },
            { label: 'Jeg vil endre ordren', action: 'delay_change' },
            { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
          ],
        });
        return;
      }

      this._appendChatMessage(
        'Ordren er registrert, men sporingsnummer er ikke opprettet ennå. Du får automatisk sporingslenke så snart pakken sendes.'
      );

      this._renderChatActionStack({
        links,
        buttons: [
          { label: 'Kontakt oss', action: 'kontakt', silent: true },
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
        ],
      });
    } catch (error) {
      this._appendChatMessage('Beklager, jeg fikk ikke hentet ordrestatus akkurat nå. Prøv igjen om litt.');
      this._renderChatActionStack({
        buttons: [
          { label: 'Prøv igjen', action: 'order_tracking', silent: true },
          { label: 'Kontakt oss', action: 'kontakt', silent: true },
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
        ],
      });
    }
  }

  _handleDefectOrderSubmit(form) {
    console.log('[FloatingNav] _handleDefectOrderSubmit called');
    let orderNumber = this._getChatFormValue(form);
    console.log('[FloatingNav] Order number:', orderNumber);
    
    if (!orderNumber) {
      console.log('[FloatingNav] No order number entered');
      this._appendChatMessage('Vennligst skriv inn ordrenummeret ditt.');
      return;
    }

    // Auto-prefix # if not already present
    if (!orderNumber.startsWith('#')) {
      orderNumber = `#${orderNumber}`;
    }

    this.chatSession = { flow: 'defect', step: 'await_issue_type', data: { orderNumber } };
    this._appendChatMessage(orderNumber, { sender: 'user', isDelayed: false });
    this._appendChatMessage('Hva er problemet?', { sender: 'bot', isDelayed: true });

    // Schedule rendering of buttons after the bot message delay
    console.log('[FloatingNav] Scheduling button render in 500ms');
    setTimeout(() => {
      console.log('[FloatingNav] Rendering action buttons for defect flow');
      this._renderChatActionStack({
        buttons: [
          { label: 'Manglende del(er)', action: 'defect_type_missing' },
          { label: 'Defekt del(er)', action: 'defect_type_defect' },
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
        ],
      });
    }, 500);
  }

  _handleDefectDescriptionSubmit(form) {
    const description = this._getChatFormValue(form);
    if (!description) return;

    if (description.length < 8) {
      this._appendChatMessage('Kan du beskrive feilen litt mer detaljert før vi fortsetter?');
      return;
    }

    this.chatSession.data.description = description;
    this.chatSession.step = 'await_email_or_images';
    this._appendChatMessage(description, { sender: 'user' });

    const knownCustomerEmail = this.chatConfig?.customerEmail?.trim();
    if (knownCustomerEmail) {
      this.chatSession.data.customerEmail = knownCustomerEmail;
      this.chatSession.data.customerName = this.chatConfig?.customerName || '';
      this._appendChatMessage(`Supert. Jeg bruker ${knownCustomerEmail} for bekreftelse på saken.`);
      this._appendChatMessage('Last opp 2–3 bilder av feilen (maks 25 MB per bilde).');
      this.chatSession.step = 'await_images';
      this._renderChatImageForm();
      return;
    }

    this._appendChatMessage('Hvilken e-postadresse skal vi sende bekreftelse til?');
    this.chatSession.step = 'await_email';
    this._renderChatForm({
      submitAction: 'defect_email',
      label: 'E-postadresse',
      placeholder: 'navn@epost.no',
      buttonLabel: 'Fortsett',
      inputType: 'email',
    });
  }

  _handleDefectEmailSubmit(form) {
    const email = this._getChatFormValue(form);
    if (!email) return;

    this.chatSession.data.customerEmail = email;
    this._appendChatMessage(email, { sender: 'user' });
    this._appendChatMessage('Takk! Last opp 2–3 bilder av feilen (maks 25 MB per bilde).');
    this.chatSession.step = 'await_images';
    this._renderChatImageForm();
  }

  async _handleDefectImagesSubmit(form) {
    const fileInput = form.querySelector('[name="chat_files"]');
    const files = Array.from(fileInput?.files || []);

    if (files.length < 2 || files.length > 3) {
      this._appendChatMessage('Legg ved mellom 2 og 3 bilder for å sende reklamasjonen.');
      return;
    }

    const maxSizeBytes = 25 * 1024 * 1024;
    const tooLargeFile = files.find((file) => file.size > maxSizeBytes);
    if (tooLargeFile) {
      this._appendChatMessage(`Filen «${tooLargeFile.name}» er større enn 25 MB. Velg mindre filer.`);
      return;
    }

    this.chatSession.data.files = files;
    this._appendChatMessage(`${files.length} bilder vedlagt.`, { sender: 'user' });
    this._appendChatMessage('Takk! Sender inn saken din nå ...');
    this.chatActions.innerHTML = '';

    try {
      const result = await this._submitDefectTicket({
        orderNumber: this.chatSession.data.orderNumber,
        issueType: this.chatSession.data.issueType,
        description: this.chatSession.data.description,
        customerEmail: this.chatSession.data.customerEmail || '',
        customerName: this.chatSession.data.customerName || '',
        files,
      });

      const referenceLine = result.ticketReference ? ` Referanse: ${result.ticketReference}.` : '';
      const confirmationLine = result.customerNotified
        ? `Vi har sendt en bekreftelse til ${this.chatSession.data.customerEmail}.`
        : 'Bekreftelse sendes på e-post så snart saken er ferdig registrert.';

      this._appendChatMessage(
        `Vi har mottatt reklamasjonssaken din for ordre ${this.chatSession.data.orderNumber}.${referenceLine}\n\nBeklager ulempen — vi jobber med en løsning (typisk utsending av manglende eller ny del).\n\n${confirmationLine}`
      );

      this._renderChatActionStack({
        buttons: [
          { label: 'Registrer en ny reklamasjon', action: 'defect', silent: true },
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
        ],
      });
    } catch (error) {
      this._appendChatMessage(
        `Beklager, jeg fikk ikke sendt saken nå. Prøv igjen, eller kontakt ${this.chatConfig.supportEmail}.`
      );
      this._renderChatActionStack({
        buttons: [
          { label: 'Prøv opplasting på nytt', action: 'defect_retry_upload', silent: true },
          { label: 'Kontakt oss', action: 'kontakt', silent: true },
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
        ],
      });
    }
  }

  _startGeneralQuestionFlow() {
    this.chatSession = { flow: 'general_question', step: 'await_name', data: {} };
    this._appendChatMessage('Selvfølgelig! Hva heter du?');
    this._renderChatForm({
      submitAction: 'general_question_name',
      label: 'Navn',
      placeholder: 'Ditt navn',
      buttonLabel: 'Fortsett',
    });
  }

  _handleGeneralQuestionNameSubmit(form) {
    const name = this._getChatFormValue(form);
    if (!name) return;

    this.chatSession.data.customerName = name;
    this._appendChatMessage(name, { sender: 'user' });
    this._appendChatMessage('Hva er e-postadressen din?');
    this.chatSession.step = 'await_email';
    this._renderChatForm({
      submitAction: 'general_question_email',
      label: 'E-postadresse',
      placeholder: 'navn@epost.no',
      buttonLabel: 'Fortsett',
      inputType: 'email',
    });
  }

  _handleGeneralQuestionEmailSubmit(form) {
    const email = this._getChatFormValue(form);
    if (!email) return;

    this.chatSession.data.customerEmail = email;
    this._appendChatMessage(email, { sender: 'user' });
    this._appendChatMessage('Har du et ordrenummer? (valgfritt)');
    this.chatSession.step = 'await_order_number';
    this._renderChatForm({
      submitAction: 'general_question_order_number',
      label: 'Ordrenummer',
      placeholder: '#1234',
      buttonLabel: 'Fortsett',
    });
  }

  _handleGeneralQuestionOrderNumberSubmit(form) {
    const orderNumber = this._getChatFormValue(form);
    this.chatSession.data.orderNumber = orderNumber || '';
    
    if (orderNumber) {
      this._appendChatMessage(orderNumber, { sender: 'user' });
    } else {
      this._appendChatMessage('Ingen ordrenummer', { sender: 'user' });
    }
    
    this._appendChatMessage('Hva er ditt spørsmål?');
    this.chatSession.step = 'await_inquiry';
    this._renderChatForm({
      submitAction: 'general_question_inquiry',
      label: 'Spørsmål',
      placeholder: 'Beskriv ditt spørsmål...',
      buttonLabel: 'Send',
      multiline: true,
    });
  }

  async _handleGeneralQuestionInquirySubmit(form) {
    const inquiry = this._getChatFormValue(form);
    if (!inquiry) return;

    if (inquiry.length < 8) {
      this._appendChatMessage('Kan du beskrive spørsmålet ditt litt mer detaljert?');
      return;
    }

    this.chatSession.data.inquiry = inquiry;
    this._appendChatMessage(inquiry, { sender: 'user' });
    this._appendChatMessage('Takk! Sender inn spørsmålet ditt nå ...');
    this.chatActions.innerHTML = '';

    try {
      await this._sendGeneralQuestionContact({
        customerName: this.chatSession.data.customerName || '',
        customerEmail: this.chatSession.data.customerEmail || '',
        orderNumber: this.chatSession.data.orderNumber || '',
        inquiry: this.chatSession.data.inquiry || '',
      });

      this._appendChatMessage(
        `Takk for ditt spørsmål! Vi har mottatt meldingen din og vil svare deg innen 1 arbeidsdag på ${this.chatSession.data.customerEmail}.`
      );

      this._renderChatActionStack({
        buttons: [
          { label: 'Stille et nytt spørsmål', action: 'general_question', silent: true },
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
        ],
      });
    } catch (error) {
      this._appendChatMessage(
        `Beklager, jeg fikk ikke sendt spørsmålet nå. Prøv igjen, eller kontakt ${this.chatConfig.supportEmail}.`
      );
      this._renderChatActionStack({
        buttons: [
          { label: 'Prøv på nytt', action: 'general_question', silent: true },
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
        ],
      });
    }
  }

  async _sendGeneralQuestionContact({ customerName, customerEmail, orderNumber, inquiry }) {
    // Previously posted to Shopify's /contact, which meant a chatbot question
    // arrived as an email and never as a ticket. It now goes through the same
    // pipeline as every other CS surface.
    const email = customerEmail || this.csConfig?.supportEmail || 'post@kulkid.no';

    try {
      return await this._submitCustomerServiceTicket({
        name: customerName || 'KUL KID chatbot',
        email,
        body: inquiry || '(ingen beskrivelse)',
        subject: 'Annet spørsmål',
        orderNumber,
        sourceChannel: 'floating_nav_chatbot',
      });
    } catch (error) {
      console.error('Chatbot question submission failed:', error);
      throw new Error('Kunne ikke sende spørsmål');
    }
  }

  _handleDelayWaitAction() {
    const delayedItems = this.chatSession?.data?.delayedItems || [];
    const knownEta = delayedItems
      .map((item) => item.eta)
      .filter(Boolean)
      .join(', ');

    const etaMessage = knownEta ? ` Forventet restock: ${knownEta}.` : '';
    this._appendChatMessage(
      `Supert, da lar vi ordren stå aktiv og sender sporingslenke så snart den er sendt.${etaMessage}`
    );
    this._renderChatActionStack({
      buttons: [{ label: 'Til hovedmeny', action: 'chat_restart', silent: true }],
    });
  }

  _handleDelayChangeAction() {
    const manageOrderUrl = this.chatSession?.data?.orderStatus?.manageOrderUrl;
    this._appendChatMessage(
      `Skjønner. Kontakt oss på ${this.chatConfig.supportEmail}, så hjelper vi deg med å endre ordren raskt.`
    );
    this._renderChatActionStack({
      links: manageOrderUrl ? [{ label: '✏️ Endre ordren nå', href: manageOrderUrl }] : [],
      buttons: [
        { label: 'Kontakt oss', action: 'kontakt', silent: true },
        { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
      ],
    });
  }

  _renderChatActionStack({ links = [], buttons = [] } = {}) {
    if (!this.chatActions) return;

    const stack = document.createElement('div');
    stack.className = 'floating-nav__chat-action-stack';

    if (links.length > 0) {
      const linkWrap = document.createElement('div');
      linkWrap.className = 'floating-nav__chat-options';
      links.forEach((link) => {
        linkWrap.append(this._createChatOptionLink(link));
      });
      stack.append(linkWrap);
    }

    if (buttons.length > 0) {
      const buttonWrap = document.createElement('div');
      buttonWrap.className = 'floating-nav__chat-options';
      buttons.forEach((button) => {
        buttonWrap.append(this._createChatActionButton(button));
      });
      stack.append(buttonWrap);
    }

    this.chatActions.innerHTML = '';
    this.chatActions.append(stack);
    this._scrollChatThreadToBottom();
  }

  _renderChatForm({
    submitAction,
    label,
    placeholder = '',
    prefixLabel = '',
    buttonLabel = 'Fortsett',
    inputType = 'text',
    multiline = false,
  }) {
    if (!this.chatActions) return;

    const form = document.createElement('form');
    form.className = 'floating-nav__chat-form';
    form.dataset.chatFormAction = submitAction;
    form.setAttribute('autocomplete', 'off');

    const labelEl = document.createElement('label');
    labelEl.className = 'floating-nav__chat-form-label';
    labelEl.textContent = label;
    form.append(labelEl);

    // If there's a prefix label, wrap input with a prefix container
    let inputWrapper = null;
    if (prefixLabel && !multiline) {
      inputWrapper = document.createElement('div');
      inputWrapper.className = 'floating-nav__chat-input-wrapper';
      const prefix = document.createElement('span');
      prefix.className = 'floating-nav__chat-input-prefix';
      prefix.textContent = prefixLabel;
      inputWrapper.append(prefix);
      form.append(inputWrapper);
    }

    const input = multiline ? document.createElement('textarea') : document.createElement('input');
    input.className = multiline ? 'floating-nav__chat-textarea' : 'floating-nav__chat-input';
    input.name = 'chat_input';
    input.placeholder = placeholder;
    input.required = true;
    input.autocomplete = 'off';
    if (!multiline) input.type = inputType;
    
    if (inputWrapper) {
      inputWrapper.append(input);
    } else {
      form.append(input);
    }

    const actions = document.createElement('div');
    actions.className = 'floating-nav__chat-form-actions';

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'floating-nav__btn floating-nav__btn--primary';
    submit.textContent = buttonLabel;

    const back = this._createChatGhostButton();

    actions.append(submit, back);
    form.append(actions);

    // Add direct submit listener to the form
    console.log('[FloatingNav] Creating form with action:', submitAction);
    form.addEventListener('submit', (event) => {
      console.log('[FloatingNav] Form submit event fired for action:', submitAction);
      this._handleChatActionSubmit(event);
    });
    submit.addEventListener('click', (event) => {
      console.log('[FloatingNav] Submit button clicked for action:', submitAction, { eventType: event.type });
    });

    this.chatActions.innerHTML = '';
    this.chatActions.append(form);
    input.focus();
    this._scrollChatThreadToBottom();
  }

  _renderChatImageForm() {
    if (!this.chatActions) return;

    const form = document.createElement('form');
    form.className = 'floating-nav__chat-form';
    form.dataset.chatFormAction = 'defect_images';
    form.setAttribute('autocomplete', 'off');

    const label = document.createElement('label');
    label.className = 'floating-nav__chat-form-label';
    label.textContent = 'Legg ved bilder (2–3 stk)';
    form.append(label);

    const input = document.createElement('input');
    input.className = 'floating-nav__chat-input floating-nav__chat-input--file';
    input.type = 'file';
    input.name = 'chat_files';
    input.accept = 'image/*';
    input.multiple = true;
    input.required = true;
    form.append(input);

    const helpText = document.createElement('p');
    helpText.className = 'floating-nav__chat-help-text';
    helpText.textContent = 'Maks 25 MB per bilde. Last opp 2 eller 3 bilder.';
    form.append(helpText);

    const actions = document.createElement('div');
    actions.className = 'floating-nav__chat-form-actions';

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'floating-nav__btn floating-nav__btn--primary';
    submit.textContent = 'Send reklamasjon';

    const back = this._createChatGhostButton();

    actions.append(submit, back);
    form.append(actions);

    // Add direct submit listener to the form
    form.addEventListener('submit', (event) => this._handleChatActionSubmit(event));

    this.chatActions.innerHTML = '';
    this.chatActions.append(form);
    this._scrollChatThreadToBottom();
  }

  _appendChatMessage(message, { sender = 'bot', isDelayed = true } = {}) {
    if (!this.chatThread || !message) return;

    const appendMessage = () => {
      this._removeTypingIndicator();
      const bubble = document.createElement('div');
      bubble.className = `floating-nav__chat-message floating-nav__chat-message--${sender}`;
      bubble.textContent = String(message).trim();

      if (sender === 'bot') {
        bubble.classList.add('floating-nav__chat-message--answer');
      }

      this.chatThread.append(bubble);
      this._scrollChatThreadToBottom();
    };

    if (sender === 'bot' && isDelayed) {
      this._showTypingIndicator();
      // Delay based on message length: 400ms + 20ms per character (up to 2 seconds)
      const messageLength = String(message).length;
      const delay = Math.min(400 + messageLength * 20, 2000);
      setTimeout(appendMessage, delay);
    } else {
      appendMessage();
    }
  }

  _showTypingIndicator() {
    if (this._typingIndicator) return;

    const indicator = document.createElement('div');
    indicator.className = 'floating-nav__chat-message floating-nav__chat-message--bot floating-nav__chat-typing';
    indicator.innerHTML = '<span></span><span></span><span></span>';
    indicator.setAttribute('aria-label', 'Bot is typing...');

    this.chatThread.append(indicator);
    this._typingIndicator = indicator;
    this._scrollChatThreadToBottom();
  }

  _removeTypingIndicator() {
    if (this._typingIndicator) {
      this._typingIndicator.remove();
      this._typingIndicator = null;
    }
  }

  _scrollChatThreadToBottom() {
    if (!this.chatThread) return;
    window.requestAnimationFrame(() => {
      this.chatThread.scrollTop = this.chatThread.scrollHeight;
    });
  }

  async _lookupOrderStatus(orderNumber) {
    const endpoint = this.chatConfig?.orderStatusEndpoint?.trim();

    if (!endpoint) {
      const fallbackTrackingUrl = this._buildTrackingUrl(orderNumber);
      if (!fallbackTrackingUrl) {
        return { found: false, orderNumber };
      }

      return {
        found: true,
        orderNumber,
        statusText: `Ordre ${orderNumber} er registrert. Åpne lenken under for oppdatert sporingsstatus.`,
        hasTracking: true,
        trackingUrl: fallbackTrackingUrl,
        trackingNumber: '',
        items: [],
        delayedItems: [],
        manageOrderUrl: '',
      };
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        order_number: orderNumber,
        source: 'floating_nav_chatbot',
      }),
    });

    const isJsonResponse = response.headers.get('content-type')?.includes('application/json');
    const payload = isJsonResponse ? await response.json().catch(() => ({})) : {};

    if (!response.ok) {
      throw new Error(payload?.message || 'Kunne ikke hente ordrestatus');
    }

    return this._normalizeOrderStatusPayload(payload, orderNumber);
  }

  _normalizeOrderStatusPayload(payload, fallbackOrderNumber) {
    const orderNumber = String(payload.order_number || payload.orderNumber || fallbackOrderNumber || '').trim();
    const trackingUrl = payload.tracking_url || payload.trackingUrl || this._buildTrackingUrl(orderNumber);
    const trackingNumber = String(payload.tracking_number || payload.trackingNumber || '').trim();
    const hasTracking =
      typeof payload.has_tracking === 'boolean'
        ? payload.has_tracking
        : Boolean(trackingUrl || trackingNumber || payload.fulfillment_status === 'fulfilled');

    const items = Array.isArray(payload.items)
      ? payload.items
          .map((item) => {
            if (typeof item === 'string') return item;
            return item?.title || item?.name || item?.product || '';
          })
          .filter(Boolean)
      : [];

    const delayedItems = this._normalizeDelayItems(payload.delayed_items || payload.delays || []);

    return {
      found: payload.found !== false,
      orderNumber,
      statusText: payload.status_text || payload.status_message || payload.status || '',
      hasTracking,
      trackingUrl,
      trackingNumber,
      items,
      delayedItems,
      manageOrderUrl: payload.manage_order_url || payload.order_update_url || payload.change_order_url || '',
    };
  }

  _normalizeDelayItems(rawItems) {
    if (!Array.isArray(rawItems)) return [];

    return rawItems
      .map((item) => {
        if (typeof item === 'string') {
          return { product: item, eta: '', message: '' };
        }

        const product = item?.product || item?.title || item?.name || '';
        return {
          product: String(product).trim(),
          eta: String(item?.eta || item?.expected_date || item?.expected_restock_date || '').trim(),
          message: String(item?.message || item?.reason || '').trim(),
        };
      })
      .filter((item) => item.product);
  }

  _collectDelayedItems(orderStatus) {
    if (Array.isArray(orderStatus?.delayedItems) && orderStatus.delayedItems.length > 0) {
      return orderStatus.delayedItems;
    }

    if (!Array.isArray(this.delayFeedEntries) || this.delayFeedEntries.length === 0) {
      return [];
    }

    const orderItems = Array.isArray(orderStatus?.items) ? orderStatus.items : [];
    if (orderItems.length === 0) return [];

    const normalizedItems = orderItems.map((item) => String(item).toLowerCase());

    return this.delayFeedEntries
      .filter((entry) => {
        const productKey = entry.product.toLowerCase();
        return productKey === '*' || normalizedItems.some((itemName) => itemName.includes(productKey));
      })
      .map((entry) => ({
        product: entry.product === '*' ? 'Deler i ordren' : entry.product,
        eta: entry.eta,
        message: entry.message,
      }));
  }

  _parseDelayFeed(rawFeed) {
    if (!rawFeed) return [];

    const feed = String(rawFeed).trim();
    if (!feed) return [];

    try {
      const parsed = JSON.parse(feed);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => ({
          product: String(item?.product || item?.sku || '').trim(),
          eta: String(item?.eta || item?.expected_date || '').trim(),
          message: String(item?.message || '').trim(),
        }))
        .filter((item) => item.product);
    } catch (error) {
      return [];
    }
  }

  async _submitDefectTicket(ticket) {
    if (!this.chatConfig?.defectTicketEndpoint) {
      throw new Error('Defect ticket endpoint not configured');
    }

    const formData = new FormData();
    formData.append('orderNumber', ticket.orderNumber || '');
    formData.append('issueType', ticket.issueType === 'defekt_del' ? 'defect' : 'missing_item');
    formData.append('description', ticket.description || '');
    formData.append('customerEmail', ticket.customerEmail || '');
    formData.append('customerName', ticket.customerName || '');

    // Append files
    if (Array.isArray(ticket.files)) {
      ticket.files.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
    }

    const response = await fetch(this.chatConfig.defectTicketEndpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.chatConfig.supabaseAnonKey}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
      success: data.success,
      ticketId: data.ticketId,
      ticketReference: data.ticketReference,
      customerNotified: data.customerNotified !== false,
      message: data.message,
    };
  }

  async _sendDefectFallbackContact(ticket, ticketReference = '') {
    // Reached when defect-ticket-submit itself failed (usually the image
    // upload). The customer's report must still become a ticket, minus the
    // attachments — support follows up for those.
    const messageLines = [
      'Ny reklamasjon fra floating-nav chatbot (fallback — vedlegg mangler).',
      `Ordrenummer: ${ticket.orderNumber || '-'}`,
      `Sakstype: ${ticket.issueType || '-'}`,
      `Kunde-e-post: ${ticket.customerEmail || '-'}`,
      `Beskrivelse: ${ticket.description || '-'}`,
      `Vedlegg kunden forsøkte å sende: ${(ticket.files || []).map((file) => file.name).join(', ') || '-'}`,
      ticketReference ? `Ekstern referanse: ${ticketReference}` : '',
      'Vennligst be kunden sende bildene på nytt, og opprett leverandørsak for ny/manglende del.',
    ]
      .filter(Boolean)
      .join('\n');

    try {
      return await this._submitCustomerServiceTicket({
        name: ticket.customerName || 'KUL KID chatbot',
        email: ticket.customerEmail || this.csConfig?.supportEmail || 'post@kulkid.no',
        body: messageLines,
        subject: 'Reklamasjon / defekt produkt',
        orderNumber: ticket.orderNumber,
        sourceChannel: 'floating_nav_defect_fallback',
      });
    } catch (error) {
      console.error('Failed to submit defect fallback ticket:', error);
      throw error;
    }
  }

  _renderStaticInfoResponse(action) {
    const answers = {
      toll:
        'Blir du ilagt toll ved innførsel til Norge, betaler du kravet og sender kvitteringen til post@kulkid.no sammen med ordrenummeret ditt. Vi refunderer hele beløpet innen 3 til 5 virkedager.',
      retur:
        'Du har 14 dagers angrerett + utvidet 30 dagers returrett. Send e-post til kontakt@kulkid.no med ordrenummer og årsak. Refusjon skjer normalt innen 7 virkedager til samme betalingsmetode.',
    };

    if (!answers[action]) return;

    this._appendChatMessage(answers[action], { isDelayed: true });
    // Schedule action stack rendering after the message delay
    const messageLength = answers[action].length;
    const delay = Math.min(400 + messageLength * 20, 2000);
    setTimeout(() => {
      this._renderChatActionStack({
        buttons: [
          { label: 'Til hovedmeny', action: 'chat_restart', silent: true },
          { label: 'Kontakt oss', action: 'kontakt', silent: true },
        ],
      });
    }, delay);
  }

  _createChatActionButton({ label, action, silent = false }) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'floating-nav__chat-option';
    button.dataset.chatAction = action;
    if (silent) button.dataset.chatSilent = 'true';
    button.textContent = label;
    return button;
  }

  _createChatOptionLink({ label, href }) {
    const link = document.createElement('a');
    link.className = 'floating-nav__chat-option floating-nav__chat-option--link';
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = label;
    return link;
  }

  _createChatGhostButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'floating-nav__btn floating-nav__btn--ghost';
    button.dataset.chatAction = 'chat_restart';
    button.dataset.chatSilent = 'true';
    button.textContent = 'Til hovedmeny';
    return button;
  }

  _buildTrackingUrl(orderNumber) {
    const template = String(this.chatConfig?.trackingUrlTemplate || '').trim();
    if (!template || !orderNumber) return '';

    const encodedOrderNumber = encodeURIComponent(orderNumber);
    if (template.includes('{{order_number}}')) {
      return template.replaceAll('{{order_number}}', encodedOrderNumber);
    }
    if (template.includes('{order_number}')) {
      return template.replaceAll('{order_number}', encodedOrderNumber);
    }

    return `${template}${template.includes('?') ? '&' : '?'}order_number=${encodedOrderNumber}`;
  }

  _getChatFormValue(form) {
    const input = form.querySelector('[name="chat_input"]');
    return String(input?.value || '').trim();
  }

  _handleContactSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);
    const submitButton = form.querySelector('[type="submit"]');
    const errorNode = form.querySelector('[data-contact-error]');
    const resetButton = () => {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Send melding';
      }
    };

    if (errorNode) {
      errorNode.hidden = true;
      errorNode.textContent = '';
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sender...';
    }

    const contactName = String(formData.get('contact[name]') || '').trim();
    const contactEmail = String(formData.get('contact[email]') || '').trim();
    const contactBody = String(formData.get('contact[body]') || '').trim();
    const contactOrderNumber = String(formData.get('contact[order_number]') || '').trim();
    // Drives inquiry_type on the ticket, so the dashboard queue can be
    // filtered by category instead of everything landing in "spørsmål".
    const contactSubject = String(formData.get('contact[subject]') || '').trim();

    if (!contactEmail || !contactBody) {
      if (errorNode) {
        errorNode.textContent = 'Fyll inn e-postadresse og beskrivelse.';
        errorNode.hidden = false;
      }
      resetButton();
      return;
    }

    this._submitCustomerServiceTicket({
      name: contactName,
      email: contactEmail,
      body: contactBody,
      subject: contactSubject,
      orderNumber: contactOrderNumber,
      sourceChannel: 'floating_nav_contact_form',
    })
      .then((payload) => {
        const reference = payload?.ticketReference ? `<div class="floating-nav__contact-success-ref">Referanse: ${payload.ticketReference}</div>` : '';
        form.innerHTML = `
          <div class="floating-nav__contact-success">
            <div class="floating-nav__contact-success-icon">✅</div>
            <div class="floating-nav__contact-success-title">Takk!</div>
            <p class="floating-nav__contact-success-text">Vi svarer deg innen 1 virkedag.</p>
            ${reference}
          </div>
        `;
      })
      .catch((error) => {
        // The old version logged and silently re-enabled the button, so a
        // customer whose message never sent had no way to know.
        console.error('Contact form submission error:', error);
        if (errorNode) {
          errorNode.textContent = `Kunne ikke sende meldingen. Prøv igjen, eller send e-post til ${this.csConfig?.supportEmail || 'post@kulkid.no'}.`;
          errorNode.hidden = false;
        }
        resetButton();
      });
  }

  _onCartUpdate(payload) {
    const cart = this._extractCartFromPayload(payload);
    const shouldShowPostAdd = payload?.source === 'product-form';

    if (!cart || typeof cart.item_count === 'undefined' || !Array.isArray(cart.items)) {
      this._fetchCart(shouldShowPostAdd);
      return;
    }

    this._applyCartState(cart, { forcePostAdd: shouldShowPostAdd });
  }

  _extractCartFromPayload(payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.cartData && typeof payload.cartData === 'object') return payload.cartData;
    if (typeof payload.item_count !== 'undefined') return payload;
    return null;
  }

  _fetchCart(forcePostAdd = false) {
    fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => this._applyCartState(cart, { forcePostAdd }))
      .catch(() => {
        this._applyCartState({ item_count: 0, items: [], total_price: 0 }, { forcePostAdd: false });
      });
  }

  _applyCartState(cart, { forcePostAdd = false } = {}) {
    const previousCount = this.cartItemCount;

    this.cartItemCount = Number(cart.item_count) || 0;
    this.cartFirstProductId = cart.items?.[0]?.product_id ?? null;

    window.cartItemCount = this.cartItemCount;
    window.cartFirstProductId = this.cartFirstProductId;

    this._populateCart(cart);
    this._updateHomeMode();

    if ((forcePostAdd || this.cartItemCount > previousCount) && this.cartItemCount > 0) {
      this._showPostAdd();
    }
  }

  _populateCart(cart) {
    const hasItems = this.cartItemCount > 0;
    if (this.cartEmpty) this.cartEmpty.hidden = hasItems;
    if (this.cartContent) this.cartContent.hidden = !hasItems;

    if (!hasItems) {
      if (this.lineItems) this.lineItems.innerHTML = '';
      if (this.subtotal) this.subtotal.textContent = this._formatMoney(0);
      if (this.cartCount) this.cartCount.textContent = '0 varer';
      return;
    }

    if (this.lineItems && Array.isArray(cart.items)) {
      this.lineItems.innerHTML = cart.items
        .slice(0, 4)
        .map((item) => {
          const title = this._escapeHtml(item.title || '');
          const quantity = Number(item.quantity) || 0;
          const image = this._escapeHtml(item.image || '');
          const linePrice = this._formatMoney(item.final_line_price ?? item.line_price ?? 0);
          return `
            <div class="floating-nav__line-item">
              <div class="floating-nav__line-item-image">
                ${image ? `<img src="${image}" alt="${title}" width="48" height="48" loading="lazy">` : ''}
              </div>
              <div class="floating-nav__line-item-info">
                <div class="floating-nav__line-item-title">${title}</div>
                <div class="floating-nav__line-item-price">${linePrice} &times; ${quantity}</div>
              </div>
            </div>
          `;
        })
        .join('');
    }

    if (this.subtotal) {
      this.subtotal.textContent = this._formatMoney(cart.total_price ?? 0);
    }

    if (this.cartCount) {
      this.cartCount.textContent = `${this.cartItemCount} vare${this.cartItemCount === 1 ? '' : 'r'}`;
    }
  }

  _updateHomeMode() {
    const hasItems = this.cartItemCount > 0;
    if (this.homeEmpty) this.homeEmpty.hidden = hasItems;
    if (this.homeCart) this.homeCart.hidden = !hasItems;

    if (hasItems) {
      this._ensureRecommendations();
    } else {
      // Hide carousel when cart is empty
      if (this.recommendationCarousel) this.recommendationCarousel.hidden = true;
    }
  }

  _ensureRecommendations() {
    if (!this.recommendationTrack || !this.recommendationCarousel) return;

    console.log('[FloatingNav] _ensureRecommendations called', {
      useOverride: this.dataset.useOverride,
      cartFirstProductId: this.cartFirstProductId,
      hasOverrideTemplate: !!this.overrideSlideTemplate,
    });

    if (this.dataset.useOverride === 'true' && this.overrideSlideTemplate) {
      if (this.recommendationTrack.children.length === 0) {
        this.recommendationTrack.append(this.overrideSlideTemplate.content.cloneNode(true));
      }
      if (this.recommendationEmpty) this.recommendationEmpty.hidden = true;
      if (this.recommendationCarousel) this.recommendationCarousel.hidden = false;
      this._refreshCarousel(this.recommendationCarousel);
      return;
    }

    if (!this.cartFirstProductId) {
      this._renderRecommendationEmpty();
      return;
    }

    if (
      this.lastRecommendationSeedProductId === this.cartFirstProductId &&
      this.recommendationTrack.children.length > 0
    ) {
      return;
    }

    this.lastRecommendationSeedProductId = this.cartFirstProductId;

    fetch(`${window.Shopify?.routes?.root ?? '/'}recommendations/products.json?product_id=${this.cartFirstProductId}&limit=8&intent=related`)
      .then((response) => response.json())
      .then((data) => {
        const currentProductId = Number(this.cartFirstProductId);
        const products = (data.products || []).filter((product) => Number(product.id) !== currentProductId);

        if (products.length === 0) {
          this._renderRecommendationEmpty();
          return;
        }

        this._renderRecommendationSlides(products.slice(0, 8));
      })
      .catch(() => {
        this._renderRecommendationEmpty();
      });
  }

  _renderRecommendationSlides(products) {
    if (!this.recommendationTrack) return;

    this.recommendationTrack.innerHTML = products
      .map((product, index) => {
        const title = this._escapeHtml(product.title || '');
        const handle = this._escapeHtml(product.handle || '');
        const imageUrl = this._escapeHtml(this._productImageUrl(product));
        const priceCents = this._productPrice(product);
        const compareAtPriceCents = this._productComparePrice(product);
        const showComparePrice = compareAtPriceCents > priceCents;

        return `
          <div class="floating-nav__collection-slide" data-floating-slide-index="${index}">
            <div class="card-wrapper card-wrapper--product card-wrapper--collage">
              <div class="card">
                <a href="/products/${handle}" class="card-product card-product--collage" aria-label="${title}">
                  <div class="card-product__media">
                    ${
                      imageUrl
                        ? `<img class="card-product__image" src="${imageUrl}" alt="${title}" loading="lazy" width="600" height="600">`
                        : '<div class="floating-nav__recommendation-image-placeholder"></div>'
                    }
                  </div>
                  <div class="card-product__info">
                    <h3 class="card-product__title">${title}</h3>
                    <div class="card-product__price-row">
                      <span class="card-product__price${showComparePrice ? ' card-product__price--sale' : ''}">
                        ${this._formatMoney(priceCents)}
                      </span>
                      ${
                        showComparePrice
                          ? `<span class="card-product__price card-product__price--compare">${this._formatMoney(compareAtPriceCents)}</span>`
                          : ''
                      }
                    </div>
                  </div>
                </a>
                <div class="floating-nav__recommendation-cta-wrap">
                  <a href="/products/${handle}" class="card-product__atc">Se produkt</a>
                </div>
              </div>
            </div>
          </div>
        `;
      })
      .join('');

    // Hide empty state when slides are present
    if (this.recommendationEmpty) {
      this.recommendationEmpty.hidden = true;
      this.recommendationEmpty.style.display = 'none';
    }
    if (this.recommendationCarousel) this.recommendationCarousel.hidden = false;
    this._refreshCarousel(this.recommendationCarousel);
  }

  _renderRecommendationEmpty() {
    if (this.recommendationTrack) this.recommendationTrack.innerHTML = '';
    if (this.recommendationEmpty) {
      this.recommendationEmpty.hidden = false;
      this.recommendationEmpty.style.display = '';
    }
    if (this.recommendationCarousel) this.recommendationCarousel.hidden = true;
  }

  _showPostAdd() {
    if (!this.postAddBanner) return;

    this.postAddBanner.hidden = false;
    if (this.postAddTimeout) window.clearTimeout(this.postAddTimeout);

    this.postAddTimeout = window.setTimeout(() => {
      this.postAddBanner.hidden = true;
    }, 1500);
  }

  _initCarousels() {
    this.querySelectorAll('[data-floating-carousel]').forEach((carousel) => {
      this._initCarousel(carousel);
    });
  }

  _initCarousel(carousel) {
    if (!carousel || this._carouselStates.has(carousel)) return;

    const track = carousel.querySelector('.floating-nav__collection-track');
    if (!track) return;

    const getSlides = () => Array.from(track.querySelectorAll('.floating-nav__collection-slide'));
    const getGap = () => {
      const styles = window.getComputedStyle(track);
      return parseFloat(styles.columnGap || styles.gap || '0') || 0;
    };
    const getStepSize = () => {
      const firstSlide = getSlides()[0];
      if (!firstSlide) return 0;
      return firstSlide.getBoundingClientRect().width + getGap();
    };
    const getMaxScroll = () => Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
    const getDelay = () => (this.isHoverDevice ? 1900 : 2300);

    const state = {
      isHovering: false,
      isFocused: false,
      pauseUntil: 0,
      intervalId: null,
      destroyed: false,
      prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)'),
    };

    const pauseAuto = (ms = 2400) => {
      state.pauseUntil = Date.now() + ms;
    };

    const canAutoScroll = () => {
      if (state.destroyed) return false;
      if (state.prefersReducedMotion.matches) return false;
      if (document.hidden) return false;
      if (state.isHovering || state.isFocused) return false;
      if (Date.now() < state.pauseUntil) return false;
      if (getSlides().length < 2) return false;
      return true;
    };

    const stopAuto = () => {
      if (state.intervalId) {
        window.clearInterval(state.intervalId);
        state.intervalId = null;
      }
    };

    const startAuto = () => {
      stopAuto();
      if (getSlides().length < 2) return;

      state.intervalId = window.setInterval(() => {
        if (!canAutoScroll()) return;

        const step = getStepSize();
        const maxScroll = getMaxScroll();
        if (step <= 0 || maxScroll <= 0) return;

        const next = carousel.scrollLeft + step;
        if (next >= maxScroll - 2) {
          carousel.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          carousel.scrollBy({ left: step, behavior: 'smooth' });
        }
      }, getDelay());
    };

    const onMouseEnter = () => {
      state.isHovering = true;
    };

    const onMouseLeave = () => {
      state.isHovering = false;
    };

    const onFocusIn = () => {
      state.isFocused = true;
    };

    const onFocusOut = (event) => {
      if (!carousel.contains(event.relatedTarget)) {
        state.isFocused = false;
        pauseAuto(1200);
      }
    };

    const onPointerDown = () => pauseAuto(3000);
    const onTouchStart = () => pauseAuto(3000);
    const onWheel = () => pauseAuto(2400);
    const onVisibilityChange = () => {
      if (!document.hidden) pauseAuto(600);
    };
    const onReducedMotionChange = () => {
      if (state.prefersReducedMotion.matches) {
        stopAuto();
      } else {
        startAuto();
        pauseAuto(800);
      }
    };

    const destroy = () => {
      if (state.destroyed) return;
      state.destroyed = true;
      stopAuto();
      carousel.removeEventListener('mouseenter', onMouseEnter);
      carousel.removeEventListener('mouseleave', onMouseLeave);
      carousel.removeEventListener('focusin', onFocusIn);
      carousel.removeEventListener('focusout', onFocusOut);
      carousel.removeEventListener('pointerdown', onPointerDown);
      carousel.removeEventListener('touchstart', onTouchStart);
      carousel.removeEventListener('wheel', onWheel);
      document.removeEventListener('visibilitychange', onVisibilityChange);

      if (state.prefersReducedMotion.removeEventListener) {
        state.prefersReducedMotion.removeEventListener('change', onReducedMotionChange);
      } else if (state.prefersReducedMotion.removeListener) {
        state.prefersReducedMotion.removeListener(onReducedMotionChange);
      }

      this._carouselStates.delete(carousel);
    };

    carousel.addEventListener('mouseenter', onMouseEnter);
    carousel.addEventListener('mouseleave', onMouseLeave);
    carousel.addEventListener('focusin', onFocusIn);
    carousel.addEventListener('focusout', onFocusOut);
    carousel.addEventListener('pointerdown', onPointerDown);
    carousel.addEventListener('touchstart', onTouchStart, { passive: true });
    carousel.addEventListener('wheel', onWheel, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    if (state.prefersReducedMotion.addEventListener) {
      state.prefersReducedMotion.addEventListener('change', onReducedMotionChange);
    } else if (state.prefersReducedMotion.addListener) {
      state.prefersReducedMotion.addListener(onReducedMotionChange);
    }

    this._carouselStates.set(carousel, { destroy, startAuto, pauseAuto });
    startAuto();
  }

  _refreshCarousel(carousel) {
    if (!carousel) return;
    this._destroyCarousel(carousel);
    this._initCarousel(carousel);
  }

  _destroyCarousel(carousel) {
    const state = this._carouselStates.get(carousel);
    if (state?.destroy) state.destroy();
  }

  _productImageUrl(product) {
    if (!product) return '';

    const firstImage = product.images?.[0];
    if (typeof firstImage === 'string') return firstImage;
    if (firstImage?.src) return firstImage.src;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.url) return product.featured_image.url;
    return '';
  }

  _productPrice(product) {
    if (!product) return 0;
    const variant = product.variants?.[0];
    const variantPrice = Number(variant?.price);
    if (!Number.isNaN(variantPrice) && variantPrice > 0) return variantPrice;
    return Number(product.price) || 0;
  }

  _productComparePrice(product) {
    if (!product) return 0;
    const variant = product.variants?.[0];
    const variantCompare = Number(variant?.compare_at_price);
    if (!Number.isNaN(variantCompare) && variantCompare > 0) return variantCompare;
    return Number(product.compare_at_price) || 0;
  }

  _escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  _formatMoney(cents) {
    // Locale was hardcoded to nb-NO, so SEK and DKK amounts rendered with
    // Norwegian grouping and a NOK-shaped label. KK.formatMoney reads the
    // active market instead.
    if (window.KK?.formatMoney) return window.KK.formatMoney(cents);

    return new Intl.NumberFormat(document.documentElement.lang || 'nb-NO', {
      style: 'currency',
      currency: window.Shopify?.currency?.active ?? 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format((Number(cents) || 0) / 100);
  }
}

if (!customElements.get('floating-nav')) {
  customElements.define('floating-nav', FloatingNav);
}
